import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { QuestionDto, QuestionStatus } from '@ecom/contracts';
import { FilterBar, FilterField, ToggleGroup } from '@/components/filters';
import { ProductPicker } from '@/components/product-picker';
import { can } from '@/lib/session';
import { useAuthStore } from '@/stores/auth.store';
import { EMPTY_QUESTION_FILTERS, useQuestionsStore } from '@/stores/questions.store';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, date } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { CheckIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, TableScroll, Tabs, Thumb } from '@/components/page';
import { Alert, Button, ConfirmDialog, EmptyState, Pill, Skeleton, Textarea, type PillTone } from '@/components/ui';

const STATUSES: {
  value: QuestionStatus;
  key: 'questionWaiting' | 'questionPublished' | 'questionHidden';
  tone: PillTone;
}[] = [
  { value: 'PENDING', key: 'questionWaiting', tone: 'warning' },
  { value: 'PUBLISHED', key: 'questionPublished', tone: 'success' },
  { value: 'REJECTED', key: 'questionHidden', tone: 'danger' },
];

/** A tab is one status, or all of them; a hand-made mix from the filters is `custom` — no tab lit. */
type QuestionView = QuestionStatus | 'all' | 'custom';

function viewOf(statuses: QuestionStatus[]): QuestionView {
  if (statuses.length === 0) return 'all';
  return statuses.length === 1 ? statuses[0] : 'custom';
}

/**
 * Customers' questions about products (R20, ADR-047).
 *
 * Cards rather than a table, as reviews are: the customer's own words are what
 * the answer is written to, and a table row would cut them short. Answering
 * publishes; nothing is public before it is answered.
 */
export function QuestionsPage() {
  const { items, loading, loadingMore, error, nextCursor, filters, total, productName, load, loadMore } =
    useQuestionsStore();
  const waiting = useWorkspaceStore((state) => state.attention.questionsPending);
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  useEffect(() => {
    void load();
  }, [load, locale]);

  return (
    <Page title={t('questions')} subtitle={t('questionsSubtitle')} fill>
      <ListCard>
        {/* The status views up front; the filters below still take a mix. */}
        <Tabs<QuestionView>
          className="px-2"
          label={t('questions')}
          value={viewOf(filters.statuses)}
          onChange={(id) => void load({ statuses: id === 'all' || id === 'custom' ? [] : [id] })}
          items={[
            ...STATUSES.map((entry) => ({
              id: entry.value,
              label: t(entry.key),
              // The menu's own count of what is waiting, so the tab and the badge agree.
              count: entry.value === 'PENDING' && waiting !== undefined ? count(waiting, locale) : undefined,
            })),
            { id: 'all' as const, label: t('allQuestions') },
          ]}
        />
        <ListToolbar>
          <FilterBar
            search={filters.search}
            onSearch={(search) => void load({ search })}
            placeholder={t('searchQuestionsHint')}
            total={total ?? undefined}
            shown={items.length}
            chips={[
              ...filters.statuses.map((value) => ({
                key: `s-${value}`,
                label: t(STATUSES.find((entry) => entry.value === value)?.key ?? 'questionWaiting'),
                onRemove: () => void load({ statuses: filters.statuses.filter((entry) => entry !== value) }),
              })),
              ...(filters.productId
                ? [
                    {
                      key: 'product',
                      label: productName ? `${t('product')}: ${productName}` : t('product'),
                      onRemove: () => void load({ productId: '' }),
                    },
                  ]
                : []),
            ]}
            onClearAll={() => void load(EMPTY_QUESTION_FILTERS)}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FilterField label={t('filterStatus')}>
                <ToggleGroup
                  options={STATUSES.map((entry) => ({ value: entry.value, label: t(entry.key) }))}
                  selected={filters.statuses}
                  onChange={(statuses) => void load({ statuses: statuses as QuestionStatus[] })}
                />
              </FilterField>
              <div>
                {/* One product at a time: the latest choice replaces the previous one. */}
                <ProductPicker
                  label={t('product')}
                  selectedIds={filters.productId ? [filters.productId] : []}
                  onChange={(ids) => void load({ productId: ids.at(-1) ?? '' })}
                />
              </div>
            </div>
          </FilterBar>

          <p className="text-xs text-ink-muted">{t('questionsNote')}</p>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <div className="space-y-3 p-5">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState title={t('noQuestions')} body={t('noQuestionsHint')} icon={<CheckIcon />} />
        ) : (
          <TableScroll>
            <ul className="flex flex-col">
              {items.map((question) => (
                <QuestionCard key={question.id} question={question} />
              ))}
            </ul>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>
    </Page>
  );
}

/** One question: the product it is about, the customer's words, the store's answer, and whether it shows. */
function QuestionCard({ question }: { question: QuestionDto }) {
  const moderate = useQuestionsStore((state) => state.moderate);
  const busy = useQuestionsStore((state) => state.busyIds.includes(question.id));
  const mayOpenProducts = useAuthStore((state) => can(state.user, 'products:read'));
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [hiding, setHiding] = useState(false);

  const status = STATUSES.find((entry) => entry.value === question.status);
  const product = (
    <>
      <Thumb src={mediaUrl(question.product.imageUrl)} alt="" size="xs" />
      <span className="min-w-0 truncate">{question.product.name}</span>
    </>
  );

  return (
    <li className="border-b border-line p-5">
      <div className="flex flex-wrap items-center gap-2">
        {mayOpenProducts ? (
          <Link
            to={`/products/${question.product.id}`}
            className="flex min-w-0 items-center gap-2 text-sm font-semibold text-ink hover:text-brand-600"
          >
            {product}
          </Link>
        ) : (
          <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-ink">{product}</span>
        )}
        <Pill tone={status?.tone ?? 'neutral'}>{t(status?.key ?? 'questionWaiting')}</Pill>
        <span className="ms-auto text-xs text-ink-muted">{date(question.createdAt, locale)}</span>
      </div>

      <p className="mt-3 whitespace-pre-line font-medium text-ink">{question.body}</p>
      <p className="mt-1 text-xs text-ink-muted">
        {question.customerId ? (
          <Link to={`/customers/${question.customerId}`} className="hover:text-brand-600">
            {question.askerName ?? t('customerWithoutName')}
          </Link>
        ) : (
          (question.askerName ?? t('customerWithoutName'))
        )}
      </p>

      <AnswerEditor question={question} busy={busy} />

      <div className="mt-4 flex gap-2">
        {question.status === 'REJECTED' && question.answer ? (
          <Button variant="secondary" disabled={busy} onClick={() => void moderate(question.id, 'PUBLISHED')}>
            {t('publishAgain')}
          </Button>
        ) : null}
        {question.status !== 'REJECTED' ? (
          <Button variant="ghost" disabled={busy} onClick={() => setHiding(true)}>
            {t('hideQuestion')}
          </Button>
        ) : null}
      </div>

      {/* Hiding is undone from the Hidden tab, but a published answer leaves
          the product page the moment it is confirmed. */}
      <ConfirmDialog
        open={hiding}
        title={t('questionHideTitle')}
        body={question.answer ? t('questionHideBodyAnswered') : t('questionHideBodyWaiting')}
        confirmLabel={t('hideQuestion')}
        cancelLabel={t('cancel')}
        busy={busy}
        onConfirm={async () => {
          await moderate(question.id, 'REJECTED');
          setHiding(false);
        }}
        onCancel={() => setHiding(false)}
      />
    </li>
  );
}

/**
 * The store's answer: written by a person, published with the question, dated.
 * A first answer mails the asker; a rewrite does not.
 */
function AnswerEditor({ question, busy }: { question: QuestionDto; busy: boolean }) {
  const answer = useQuestionsStore((state) => state.answer);
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  // A waiting question opens with the box ready: answering is the whole job.
  const [draft, setDraft] = useState<string | null>(question.answer ? null : '');

  if (draft !== null) {
    return (
      <div className="mt-3 flex flex-col gap-2">
        <Textarea
          rows={3}
          maxLength={2000}
          value={draft}
          aria-label={t('storeAnswer')}
          placeholder={t('storeAnswer')}
          onChange={(event) => setDraft(event.target.value)}
        />
        <p className="text-xs text-ink-muted">{t('storeAnswerHint')}</p>
        <div className="flex gap-2">
          <Button
            disabled={busy || draft.trim().length < 2}
            onClick={async () => {
              if (await answer(question.id, draft.trim())) setDraft(null);
            }}
          >
            {busy ? t('saving') : question.answer ? t('save') : t('publishAnswer')}
          </Button>
          {question.answer ? (
            <Button variant="ghost" onClick={() => setDraft(null)}>
              {t('cancel')}
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  if (!question.answer) return null;
  return (
    <div className="mt-3 border-s-2 border-line ps-3">
      <p className="text-xs font-semibold text-ink">
        {t('storeAnswer')}
        <span className="ms-2 font-normal text-ink-muted">{date(question.answer.at, locale)}</span>
      </p>
      <p className="mt-1 whitespace-pre-line text-sm text-ink-muted">{question.answer.body}</p>
      <Button variant="ghost" className="mt-1" disabled={busy} onClick={() => setDraft(question.answer?.body ?? '')}>
        {t('editAnswer')}
      </Button>
    </div>
  );
}
